/**
 * One emoji on a category, and the two checks that keep it one.
 *
 * ── WHY THESE ARE PLAIN FUNCTIONS ──
 * The settings screen offers a curated palette AND a free-text box, because a
 * palette of forty can never hold the one somebody wants. Free text is where
 * "one emoji" stops being obvious: a flag is two code points, a skin-toned
 * hand is two, a family is seven joined by ZWJs, and `string.length` says
 * nothing useful about any of them. So the rule is stated once, here, where a
 * Node test can pin it, and the screen only reports the answer.
 *
 * The column is VARCHAR(16) and the zod limit is 16 UTF-16 units; the client
 * is STRICTER and measures bytes, because the brief is "at most 16 bytes" and
 * a family sequence (25 bytes) that the server would also refuse should be
 * refused with a sentence rather than a 400.
 */

/** The bytes a string takes in UTF-8, which is what the wire and the column see. */
export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

interface GraphemeSegmenter {
  segment(input: string): Iterable<unknown>;
}
type SegmenterCtor = new (locale?: string, options?: { granularity: 'grapheme' }) => GraphemeSegmenter;

/**
 * Whether a string is a single user-perceived character.
 *
 * `Intl.Segmenter` is the correct answer and every evergreen browser has it;
 * where it is missing the fallback counts code points after dropping the
 * joiners and modifiers that glue a sequence together, which is right for
 * every emoji in the palette and merely lenient for the exotic ones.
 */
export function isSingleGrapheme(value: string): boolean {
  if (value.length === 0) return false;
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
  if (Segmenter) {
    let count = 0;
    for (const segment of new Segmenter(undefined, { granularity: 'grapheme' }).segment(value)) {
      if (segment) count += 1;
      if (count > 1) return false;
    }
    return count === 1;
  }
  // A joiner glues the code point after it onto the one before, so both go;
  // then variation selectors, skin tones and tag characters (subdivision
  // flags), which modify rather than add; then the regional-indicator pair
  // that makes a flag collapses to one letter.
  const stripped = value
    .replace(/‍./gu, '')
    .replace(/️|[\u{1f3fb}-\u{1f3ff}]|[\u{e0020}-\u{e007f}]/gu, '')
    .replace(/[\u{1f1e6}-\u{1f1ff}]{2}/u, 'F');
  return Array.from(stripped).length <= 1;
}

/** The maximum the column, the schema and this check all agree on. */
export const EMOJI_MAX_BYTES = 16;

/**
 * Why a typed emoji cannot be saved, or null when it can.
 *
 * Empty is fine - it means "no emoji" and clears the column. The sentences
 * are for the person typing, per the house rule: "Invalid emoji" sends them
 * to guess.
 */
export function emojiProblem(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!isSingleGrapheme(trimmed)) return 'One emoji only - this is more than one character.';
  if (utf8ByteLength(trimmed) > EMOJI_MAX_BYTES) {
    return 'That one is too long to store. Pick a simpler emoji.';
  }
  return null;
}

/**
 * The curated palette on the settings screen.
 *
 * ── WHY FORTY-ODD, AND WHY THESE ──
 * Enough to cover what a small business and a household spend on and earn
 * from without scrolling, few enough to scan. Grouped by what a person is
 * looking for - money, work, home, food, getting about, people, things - so
 * the eye lands near the right tile. Every one is a single grapheme under 16
 * bytes, and a test proves it, so a palette tile can never be refused by the
 * rule beside it.
 */
export const EMOJI_PALETTE: ReadonlyArray<{ emoji: string; label: string }> = [
  // Money
  { emoji: '💰', label: 'Money bag' },
  { emoji: '💸', label: 'Money flying' },
  { emoji: '🏦', label: 'Bank' },
  { emoji: '💳', label: 'Card' },
  { emoji: '📈', label: 'Chart up' },
  { emoji: '🧾', label: 'Receipt' },
  { emoji: '🤝', label: 'Handshake' },
  { emoji: '🎁', label: 'Gift' },
  // Work
  { emoji: '💼', label: 'Briefcase' },
  { emoji: '🛠️', label: 'Tools' },
  { emoji: '🧠', label: 'Brain' },
  { emoji: '💻', label: 'Laptop' },
  { emoji: '📞', label: 'Phone' },
  { emoji: '📣', label: 'Megaphone' },
  { emoji: '🧩', label: 'Puzzle piece' },
  { emoji: '🖨️', label: 'Printer' },
  { emoji: '📎', label: 'Paperclip' },
  { emoji: '📦', label: 'Parcel' },
  { emoji: '🔁', label: 'Repeat' },
  { emoji: '↩️', label: 'Return' },
  // Home
  { emoji: '🏢', label: 'Office' },
  { emoji: '🏠', label: 'House' },
  { emoji: '💡', label: 'Light bulb' },
  { emoji: '🔧', label: 'Spanner' },
  { emoji: '🧹', label: 'Broom' },
  { emoji: '🛒', label: 'Trolley' },
  // Food
  { emoji: '🍽️', label: 'Plate' },
  { emoji: '☕', label: 'Coffee' },
  { emoji: '🍎', label: 'Apple' },
  { emoji: '🍕', label: 'Pizza' },
  // Getting about
  { emoji: '✈️', label: 'Plane' },
  { emoji: '🚗', label: 'Car' },
  { emoji: '⛽', label: 'Fuel' },
  { emoji: '🚌', label: 'Bus' },
  // People and life
  { emoji: '👥', label: 'People' },
  { emoji: '🧑‍💼', label: 'Office worker' },
  { emoji: '🏥', label: 'Hospital' },
  { emoji: '🎓', label: 'Education' },
  { emoji: '🎬', label: 'Entertainment' },
  { emoji: '🐾', label: 'Pets' },
  { emoji: '👕', label: 'Clothes' },
  { emoji: '🎉', label: 'Party' },
];
